// Client-side search for the master data tables (see components/search/DataSearchFilters).
//
// A page describes its searchable columns as fields:
//   { key, label, type: 'text' | 'select' | 'combobox', value: row => value | value[], placeholder? }
// `value` returns what the table shows for that column (an array for multi-valued columns, e.g. roles).
// A select normally offers the values found in the data; yes/no-style fields give fixed choices
// instead, as `options: [{ value, label? }]` (value '' = no filter, e.g. "All"), optionally with a
// `defaultValue` that the panel starts with and returns to on reset.
// Filters are { q, [field.key]: string }: every set field must match (AND) - text fields by
// "contains", select/combobox fields exactly - and the free-text `q` must match any field.

const norm = (v) => String(v ?? '').trim().toLowerCase();

function valuesOf(field, row) {
  const v = field.value(row);
  return (Array.isArray(v) ? v : [v]).map(norm).filter((s) => s !== '');
}

export function filterRows(rows, fields, filters) {
  const q = norm(filters.q);
  const active = fields.filter((f) => norm(filters[f.key]) !== '');
  if (!q && active.length === 0) return rows;

  return rows.filter((row) => {
    for (const field of active) {
      const want = norm(filters[field.key]);
      const values = valuesOf(field, row);
      const ok = field.type === 'text' ? values.some((v) => v.includes(want)) : values.includes(want);
      if (!ok) return false;
    }
    return !q || fields.some((field) => valuesOf(field, row).some((v) => v.includes(q)));
  });
}

/** Distinct values of a field across the rows, for its dropdown (as shown, naturally sorted). */
export function fieldOptions(rows, field) {
  const seen = new Map();
  for (const row of rows) {
    const v = field.value(row);
    for (const item of Array.isArray(v) ? v : [v]) {
      const label = String(item ?? '').trim();
      if (label && !seen.has(label.toLowerCase())) seen.set(label.toLowerCase(), label);
    }
  }
  const options = [...seen.values()].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  return field.sortDescending ? options.reverse() : options;
}

/** The filters a panel starts with: each field's defaultValue, if any. */
export function defaultDataFilters(fields) {
  return Object.fromEntries(fields.filter((f) => f.defaultValue !== undefined).map((f) => [f.key, f.defaultValue]));
}

/** Whether anything differs from the defaults (enables "Reset filters"). */
export function hasActiveDataFilters(filters, fields = []) {
  const defaults = defaultDataFilters(fields);
  const keys = new Set([...Object.keys(filters), ...Object.keys(defaults)]);
  return [...keys].some((k) => norm(filters[k]) !== norm(defaults[k]));
}
